import { useTranslation } from "react-i18next";
import { BookOpen } from "lucide-react";
import { PageRefLink } from "./PageRefLink";
import type { ScopePageSummary } from "./types";

export const ScopeSummarySection = ({
  summary,
  onNavigate,
}: {
  summary?: ScopePageSummary;
  onNavigate: (file: string, page: number) => void;
}) => {
  const { t } = useTranslation();

  if (!summary) return null;

  const sections: { labelKey: string; value?: string; items?: string[] }[] = [
    { labelKey: "summary", value: summary.summary },
    { labelKey: "scopeOfWork", value: summary.scope_of_work },
    { labelKey: "specialConditions", items: summary.special_conditions },
    { labelKey: "materialRequirements", items: summary.material_requirements },
    {
      labelKey: "standardsAndRegulations",
      items: summary.standards_and_regulations,
    },
    {
      labelKey: "warrantyAndLiability",
      value: summary.warranty_and_liability,
    },
    {
      labelKey: "timelineAndMilestones",
      value: summary.timeline_and_milestones,
    },
    { labelKey: "keyQuantities", items: summary.key_quantities },
  ];

  const hasSurveyorNotes =
    summary.surveyor_notes && summary.surveyor_notes.length > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden h-full">
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
          <BookOpen size={16} className="text-indigo-600" />
        </div>
        <h3 className="text-sm font-semibold text-gray-900">
          {t("smartSurveyor.researchReport.scopeSummary")}
        </h3>
      </div>
      <div className="p-5 space-y-4">
        {sections.map((s) => {
          if (s.value) {
            return (
              <div key={s.labelKey}>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                  {t(`smartSurveyor.researchReport.scopeFields.${s.labelKey}`)}
                </div>
                <div className="text-sm text-gray-700 leading-relaxed">
                  {s.value}
                </div>
              </div>
            );
          }
          if (s.items && s.items.length > 0) {
            return (
              <div key={s.labelKey}>
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                  {t(`smartSurveyor.researchReport.scopeFields.${s.labelKey}`)}
                </div>
                <ul className="space-y-1">
                  {s.items.map((item, i) => (
                    <li
                      key={i}
                      className="text-sm text-gray-700 flex items-start gap-2"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-300 mt-1.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          return null;
        })}
        {hasSurveyorNotes && (
          <div className="border-t border-gray-100 pt-4">
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
              {t("smartSurveyor.researchReport.scopeFields.surveyorNotes")}
            </div>
            <div className="space-y-1.5">
              {summary.surveyor_notes!.map((note, i) => (
                <div
                  key={i}
                  className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2"
                >
                  {note}
                </div>
              ))}
            </div>
          </div>
        )}
        {summary.source_pages && summary.source_pages.length > 0 && (
          <div className="border-t border-gray-100 pt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-gray-400">Source:</span>
            {summary.source_pages.map((sp, i) => (
              <PageRefLink
                key={i}
                file={sp.file}
                page={sp.page}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
